import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useNotification from '../../hooks/useNotification';
import { useAuth } from '../../hooks/useAuth';
import _Icon from '../AppIcon';
import { formatRelativeTime } from '../../utils/dateUtils';

const NotificationDropdown = ({ isOpen, setIsOpen, showViewAllLink = false, viewAllLink = '/notifications' }) => {
  const onClose = useCallback(() => {
    setIsOpen && setIsOpen(false);
  }, [setIsOpen]);
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const dropdownRef = useRef(null);
  
  const {
    notifications = [],
    unreadCount = 0,
    markAsRead,
    markAllAsRead,
    deleteNotification
  } = useNotification();

  const [filter, setFilter] = useState('all');

  // Map notification types to routes based on user role
  const getNotificationRoute = useCallback((type) => {
    const isEmployer = userProfile?.role === 'employer';
    
    const routeMap = {
      message: '/messages',
      job: isEmployer ? '/employer-dashboard?tab=jobs' : '/job-search',
      application: isEmployer ? '/employer-dashboard?tab=applications' : '/applications',
      interview: isEmployer ? '/interview-management' : '/interviews',
      system: '/dashboard',
      achievement: '/va-profile',
      analytics: isEmployer ? '/employer-dashboard?tab=analytics' : '/analytics'
    };
    
    return routeMap[type] || '/notifications';
  }, [userProfile?.role]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const getNotificationIcon = (type) => {
    const iconMap = {
      message: 'MessageSquare',
      job: 'Briefcase',
      application: 'FileText',
      interview: 'Calendar',
      system: 'Bell',
      achievement: 'Award',
      analytics: 'BarChart3'
    };
    return iconMap[type] || 'Bell';
  };

  const getNotificationColor = (type, priority) => {
    if (priority === 'high') return 'text-red-600 bg-red-50';
    if (priority === 'medium') return 'text-orange-600 bg-orange-50';
    
    const colorMap = {
      message: 'text-blue-600 bg-blue-50',
      job: 'text-green-600 bg-green-50',
      application: 'text-purple-600 bg-purple-50',
      interview: 'text-indigo-600 bg-indigo-50',
      system: 'text-gray-600 bg-gray-50',
      achievement: 'text-yellow-600 bg-yellow-50',
      analytics: 'text-teal-600 bg-teal-50'
    };
    return colorMap[type] || 'text-gray-600 bg-gray-50';
  };

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.is_read) {
        await markAsRead(notification.id);
      }
      
      // Get the route based on notification type
      const route = getNotificationRoute(notification.type);
      
      if (route) {
        navigate(route);
      }
      
      onClose();
    } catch (error) {
      console.error('Error handling notification click:', error);
      onClose();
    }
  };

  const handleMarkAllAsRead = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDeleteNotification = (e, notificationId) => {
    e.preventDefault();
    e.stopPropagation();
    deleteNotification(notificationId);
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'unread') return !notification.is_read;
    return true;
  });

  // Limit displayed notifications to 10
  const displayedNotifications = filteredNotifications.slice(0, 10);
  const hasMoreNotifications = filteredNotifications.length > 10;

  if (!isOpen) {
    return null;
  }

  return (
    <div className="absolute inset-0" style={{ pointerEvents: 'none', zIndex: 9998 }}>
      <div
        ref={dropdownRef}
        className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 flex flex-col max-h-96"
        style={{ zIndex: 9999, pointerEvents: 'auto' }}
      >
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
          <div className="flex items-center space-x-2" style={{ pointerEvents: 'auto' }}>
            {unreadCount > 0 && (
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleMarkAllAsRead(e);
                }}
                className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                style={{ pointerEvents: 'auto' }}
              >
                Mark all read
              </button>
            )}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
            >
              <_Icon name="X" size={16} />
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex space-x-1">
          {[
            { key: 'all', label: 'All', count: notifications.length },
            { key: 'unread', label: 'Unread', count: unreadCount }
          ].map(tab => (
            <button
              key={tab.key}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setFilter(tab.key);
              }}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                filter === tab.key
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              style={{ pointerEvents: 'auto' }}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                  filter === tab.key
                    ? 'bg-blue-200 text-blue-800'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Notifications List */}
      <div className="overflow-y-auto flex-1 min-h-0">
        {displayedNotifications.length === 0 ? (
          <div className="p-8 text-center">
            <_Icon name="Bell" size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-sm">
              {filter === 'unread' ? 'No unread notifications' : 
               'No notifications yet'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {displayedNotifications.map(notification => (
              <div
                key={notification.id}
                className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                  !notification.is_read ? 'bg-blue-50' : ''
                } group`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleNotificationClick(notification);
                }}
                style={{ pointerEvents: 'auto' }}
              >
                <div className="flex items-start space-x-3">
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    getNotificationColor(notification.type, notification.priority)
                  }`}>
                    {notification.avatar ? (
                      <span className="text-xs font-semibold">
                        {notification.avatar}
                      </span>
                    ) : (
                      <_Icon name={getNotificationIcon(notification.type)} size={14} />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${
                          !notification.is_read ? 'text-gray-900' : 'text-gray-700'
                        }`}>
                          {notification.title}
                        </p>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          {formatRelativeTime(notification.time)}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-1 ml-2">
                        {!notification.is_read && (
                          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        )}
                        <button
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => handleDeleteNotification(e, notification.id)}
                          className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors opacity-0 group-hover:opacity-100"
                          aria-label="Delete notification"
                        >
                          <_Icon name="X" size={12} />
                        </button>
                      </div>
                    </div>

                    {/* Priority indicator */}
                    {(notification.priority === 'high' || notification.data?.priority === 'high' || notification.data?.priority === 'urgent') && (
                      <div className="flex items-center mt-2">
                        <_Icon name="AlertCircle" size={12} className="text-red-500 mr-1" />
                        <span className="text-xs text-red-600 font-medium">High Priority</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
        <button
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            navigate('/notifications');
          }}
          type="button"
          className="w-full text-center py-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors cursor-pointer hover:bg-blue-50 rounded"
          style={{ pointerEvents: 'auto' }}
        >
          View all notifications
        </button>
      </div>
    </div>
    </div>
  );
};

export default NotificationDropdown;
